/**
 * =============================================================================
 * TimeOfDayManager - Manages the game's day/night cycle
 * =============================================================================
 * 
 * Controls the current time of day which affects:
 * - Ambient darkness level (for lighting system)
 * - Shadow direction and length
 * 
 * Currently uses local time as a mock, but is prepared to sync with server time.
 * 
 * =============================================================================
 */

export class TimeOfDayManager {
  private currentHour: number = 12;
  private useServerTime: boolean = false;
  
  // Speed multiplier for time progression (1 = real time, 24 = full day in 1 hour)
  private timeMultiplier: number = 1;
  
  /**
   * Update the current time.
   * Uses local time when not synced with server.
   */
  update(): void {
    if (!this.useServerTime) {
      const now = new Date();
      this.currentHour = now.getHours() + now.getMinutes() / 60;
    }
  }
  
  /**
   * Get the current hour (0-24, with decimals for minutes)
   */
  getCurrentHour(): number {
    return this.currentHour;
  }
  
  /**
   * Calculate ambient darkness level based on time of day.
   * @returns 0 = full daylight, 1 = complete darkness
   */
  getAmbientDarkness(): number {
    // Time ranges:
    // 6-18 = day (with dawn/dusk transitions)
    // 0-6 and 18-24 = night
    
    if (this.currentHour >= 6 && this.currentHour <= 18) {
      // Daytime - minimal darkness in central hours (8-16)
      // Dawn (6-8) and dusk (16-18) have gradual transitions
      const distFromNoon = Math.abs(this.currentHour - 12);
      
      if (distFromNoon <= 4) {
        // Full daylight (8-16)
        return 0;
      } else {
        // Dawn/dusk transition (6-8 and 16-18)
        return ((distFromNoon - 4) / 2) * 0.4;
      }
    } else {
      // Nighttime
      // Calculate hours from midnight (0-6 range normalized)
      let nightProgress: number;
      
      if (this.currentHour < 6) {
        // After midnight (0-6)
        nightProgress = (6 - this.currentHour) / 6; // 1 at midnight, 0 at 6am
      } else {
        // Before midnight (18-24)
        nightProgress = (this.currentHour - 18) / 6; // 0 at 6pm, 1 at midnight
      }
      
      // Night darkness ranges from 0.4 (dusk/dawn) to 0.85 (midnight)
      return 0.4 + nightProgress * 0.45;
    }
  }
  
  /**
   * Sync time with server.
   * @param hour - Server's current hour (0-24)
   */
  setServerTime(hour: number): void {
    this.useServerTime = true;
    this.currentHour = hour;
  }
  
  /**
   * Stop using server time and revert to local time.
   */
  useLocalTime(): void {
    this.useServerTime = false;
  }
  
  /**
   * Check if currently using server time.
   */
  isUsingServerTime(): boolean {
    return this.useServerTime;
  }
  
  /**
   * Set time multiplier for accelerated day/night cycles.
   * @param multiplier - 1 = real time, higher = faster
   */
  setTimeMultiplier(multiplier: number): void {
    this.timeMultiplier = Math.max(0.1, multiplier);
  }
  
  /**
   * Force set the current hour (useful for testing/debugging).
   * @param hour - Hour to set (0-24)
   */
  setHour(hour: number): void {
    this.currentHour = Math.max(0, Math.min(24, hour));
  }
  
  /**
   * Get a human-readable time string.
   */
  getTimeString(): string {
    const hours = Math.floor(this.currentHour);
    const minutes = Math.floor((this.currentHour - hours) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }
  
  /**
   * Check if it's currently daytime (6:00 - 18:00).
   */
  isDaytime(): boolean {
    return this.currentHour >= 6 && this.currentHour <= 18;
  }
  
  /**
   * Check if it's currently nighttime.
   */
  isNighttime(): boolean {
    return !this.isDaytime();
  }
}
